import EventAgent from '@hotmart-org-ca/event-agent-js';
import { useCallback, useMemo } from 'react';
import { useUser } from '@/common/contexts/user-context';

import {
  SYSTEM,
  EVENT_CONFIG,
  EventKey
} from '@/common/constants';

import {
  detectMobile,
  getPlatformType,
  getEventScreenUrl
} from '@/common/utils/platform';

import { SendEventParams } from '@/types';

const isProd = process.env.TARGET_ENV === 'production';
EventAgent.mode = isProd ? 'production' : 'staging';

interface UseEventAgentResponse {
  sendCustomEvent: (params: SendEventParams) => Promise<void>;
  trackEvent: (eventKey: EventKey, extraParams?: Record<string, any>) => Promise<void>;
}

export const useEventAgent = (): UseEventAgentResponse => {
  const { id: userId, ucode, isFromBrazil, entityType } = useUser();
  const userContextData = { ucode, userId, isFromBrazil, entityType };
  const isMobile = useMemo(() => detectMobile(), []);
  const platformType = getPlatformType();
  const eventScreen = getEventScreenUrl();

  const sendCustomEvent = useCallback(async ({
                                               entity = 'onboarding',
                                               action = 'interact',
                                               event = {},
                                               system = SYSTEM
                                             }: SendEventParams) => {
    try {
      if (isProd) {
        await EventAgent.send({
          system,
          entity,
          action,
          event: {
            device: isMobile ? 'mobile' : 'desktop',
            platform: platformType,
            eventScreen,
            ...event
          }
        });
      }

    } catch (error) {
      console.error('Failed to send event:', error);
    }
  }, [isMobile, platformType, eventScreen]);

  const trackEvent = useCallback(async (eventKey: EventKey, extraParams = {}): Promise<void> => {
    const config = EVENT_CONFIG[eventKey];
    await sendCustomEvent({
      event: {
        eventName: config.eventName,
        eventType: config.eventType,
        eventScreen: config.eventScreen,
        extraParams: JSON.stringify(extraParams),
        ucode: userContextData?.ucode || '',
        userId: userContextData?.userId || '',
        isFromBrazil: userContextData?.isFromBrazil || false,
        entityType: userContextData?.entityType || ''
      }
    });
  }, [sendCustomEvent, userContextData]);

  return {
    sendCustomEvent,
    trackEvent
  };
};
